import { useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { Link } from '@src/components/Link/Link';
import { isDotCom } from '@src/site';
import { acceptCookies, rejectCookies, getCookieAgreement, hasCookieConsentChoice, subscribeCookieSettingsOpen, subscribeCookieConsent } from '@src/utils/cookieConsent';
import { initAnalytics, trackEvent } from '@src/utils/analytics';
import { trackUmamiEvent } from '@src/utils/umami';
import { CookiesPic } from './CookiesPic';
import './CookieBanner.css';

const needsConsent = () => !isDotCom() && !hasCookieConsentChoice();
const serverConsent = () => false;

export function CookieBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const missingChoice = useSyncExternalStore(subscribeCookieConsent, needsConsent, serverConsent);
  useEffect(() => {
    if (isDotCom()) return;
    if (getCookieAgreement()) initAnalytics();
    return subscribeCookieSettingsOpen(() => setOpen(true));
  }, []);
  const visible = (open || missingChoice) && !location.pathname.startsWith('/open');
  useEffect(() => {
    document.body.classList.toggle('cookie-agreement-visible', visible);
    return () => document.body.classList.remove('cookie-agreement-visible');
  }, [visible]);
  if (!visible) return null;
  function accept() {
    acceptCookies();
    initAnalytics();
    trackEvent('cookies_accepted');
    trackUmamiEvent('cookies_accepted');
    setOpen(false);
  }
  function reject() {
    rejectCookies();
    trackUmamiEvent('cookies_rejected');
    setOpen(false);
  }
  return (
    <section className="cookie-banner" aria-labelledby="cookie-banner-title" data-testid="cookie-banner">
      <div className="cookie-banner__row">
        <CookiesPic className="cookie-banner__pic" />
        <div className="cookie-banner__content">
          <div className="cookie-banner__copy">
            <h2 id="cookie-banner-title">{t('cookies.title')}</h2>
            <p>{t('cookies.text')}{' '}
              <Link href="/policy#cookies" target="_blank" variant="inline" onClick={() => trackEvent('cookies_clicked')}>
                {t('cookies.more')}
              </Link>
            </p>
          </div>
          <div className="cookie-banner__actions">
            <Button variant="primary" size="small" onClick={accept} testId="cookie-accept">{t('cookies.accept')}</Button>
            <Button variant="secondary" size="small" onClick={reject} testId="cookie-necessary">{t('cookies.necessary')}</Button>
          </div>
        </div>
      </div>
    </section>
  );
}
